import { TenantStatus } from './tenant.interface.js'

/** A section of the tenant configuration console (TO-374), keyed as in its URL. */
export enum TenantConfigSection {
	GENERAL = 'general',
	SEEDING = 'seeding',
	IDENTITY = 'identity',
	ORGANIZATION = 'organization',
	FILE_EXCHANGE = 'file-exchange',
	BRAND = 'brand',
	PDF_WATERMARK = 'pdf-watermark',
	WEBHOOKS = 'webhooks',
	EMAIL = 'email',
	SMS = 'sms',
	DOMAIN = 'domain',
	WHATSAPP = 'whatsapp',
}

/** Which console the caller is in: a Super-Admin on any tenant, or a Tenant Admin on their own. */
export enum TenantConfigPlane {
	PLATFORM = 'PLATFORM',
	TENANT = 'TENANT',
}

export enum TenantConfigAccess {
	EDIT = 'EDIT',
	READ = 'READ',
}

/** Tenant lifecycle actions only the platform plane offers. */
export enum TenantPlatformAction {
	SUSPEND = 'SUSPEND',
	REACTIVATE = 'REACTIVATE',
	DELETE = 'DELETE',
}

/** The catalog, in console order. */
export const TENANT_CONFIG_SECTIONS: readonly TenantConfigSection[] = Object.values(TenantConfigSection)

const { EDIT, READ } = TenantConfigAccess

/**
 * The MVP permission matrix: each section's access per plane, `null` = hidden.
 * Domain and WhatsApp are Phase 2 and hidden on both planes.
 */
const MATRIX: Record<TenantConfigSection, Record<TenantConfigPlane, TenantConfigAccess | null>> = {
	[TenantConfigSection.GENERAL]: { [TenantConfigPlane.PLATFORM]: EDIT, [TenantConfigPlane.TENANT]: READ },
	[TenantConfigSection.SEEDING]: { [TenantConfigPlane.PLATFORM]: EDIT, [TenantConfigPlane.TENANT]: READ },
	[TenantConfigSection.IDENTITY]: { [TenantConfigPlane.PLATFORM]: EDIT, [TenantConfigPlane.TENANT]: EDIT },
	[TenantConfigSection.ORGANIZATION]: { [TenantConfigPlane.PLATFORM]: EDIT, [TenantConfigPlane.TENANT]: EDIT },
	[TenantConfigSection.FILE_EXCHANGE]: { [TenantConfigPlane.PLATFORM]: EDIT, [TenantConfigPlane.TENANT]: EDIT },
	[TenantConfigSection.BRAND]: { [TenantConfigPlane.PLATFORM]: EDIT, [TenantConfigPlane.TENANT]: READ },
	[TenantConfigSection.PDF_WATERMARK]: { [TenantConfigPlane.PLATFORM]: EDIT, [TenantConfigPlane.TENANT]: EDIT },
	[TenantConfigSection.WEBHOOKS]: { [TenantConfigPlane.PLATFORM]: EDIT, [TenantConfigPlane.TENANT]: EDIT },
	[TenantConfigSection.EMAIL]: { [TenantConfigPlane.PLATFORM]: EDIT, [TenantConfigPlane.TENANT]: EDIT },
	[TenantConfigSection.SMS]: { [TenantConfigPlane.PLATFORM]: EDIT, [TenantConfigPlane.TENANT]: EDIT },
	[TenantConfigSection.DOMAIN]: { [TenantConfigPlane.PLATFORM]: null, [TenantConfigPlane.TENANT]: null },
	[TenantConfigSection.WHATSAPP]: { [TenantConfigPlane.PLATFORM]: null, [TenantConfigPlane.TENANT]: null },
}

export interface TenantConfigManifestSection {
	key: TenantConfigSection
	access: TenantConfigAccess
}

export interface TenantConfigManifestSections {
	sections: TenantConfigManifestSection[]
	platformActions: TenantPlatformAction[]
}

/** The lifecycle action that flips a tenant's current status. */
const STATUS_ACTION: Record<TenantStatus, TenantPlatformAction> = {
	[TenantStatus.ACTIVE]: TenantPlatformAction.SUSPEND,
	[TenantStatus.SUSPENDED]: TenantPlatformAction.REACTIVATE,
}

/**
 * The visible sections (catalog order, with access) and the platform-only
 * actions for a caller on `plane` looking at a tenant in `status`. The tenant
 * plane never receives a platform action.
 */
export function buildTenantConfigManifestSections(
	plane: TenantConfigPlane,
	status: TenantStatus,
): TenantConfigManifestSections {
	const sections = TENANT_CONFIG_SECTIONS.flatMap((key) => {
		const access = MATRIX[key][plane]
		return access ? [{ key, access }] : []
	})
	const platformActions =
		plane === TenantConfigPlane.PLATFORM ? [STATUS_ACTION[status], TenantPlatformAction.DELETE] : []
	return { sections, platformActions }
}
